// Stacking order of the four side drawers — Forecast Manager (left), Chat Console (right),
// Signal Library (bottom), Question (top). The drawer the user opened MOST RECENTLY renders
// on top, rather than a fixed order — except the Signal Library, which is on top while it is
// open (useDrawerStack), so a rail panel opened after it never covers part of it.
//
// All four compete in the ROOT stacking context (the Question drawer through the top
// toolbar that hosts it), so their z-indexes are all that orders them. The slots sit in
// one band, placed against their neighbours:
//   * BELOW the two side tab strips (DRAWER_TAB_Z). The Question and Signal Library drawers
//     are full-width, so they overlap the strips; with a fixed order they were always on
//     top anyway, but once a side drawer can be raised above them, an older full-width
//     drawer left over its tab strip peeks out around the raised side drawer and cuts the
//     strip's label off. Strips above every drawer end each drawer cleanly at the strips;
//   * BELOW the Signal Library bar (SIGNAL_BAR_Z), which the Signal Library drawer slides
//     up from behind — and the transient modals (z:400+), which stay in front;
//   * two apart, so a flyover opened ON a drawer (Tracking Settings over the Forecast
//     Manager) can sit at that drawer's slot + 1 without tying the next one up.

/** The drawers, in the fixed order they used to stack (bottom → top): the starting order. */
export const DRAWERS = Object.freeze(["log", "chat", "tsd", "contract"]);

/** z-index per stacking position, bottom → top. */
export const DRAWER_Z_SLOTS = Object.freeze([64, 66, 68, 70]);

/** The left/right vertical tab strips (SideBar): above every drawer slot and its flyover. */
export const DRAWER_TAB_Z = 72;

/** A tab strip's outer width (26px + its 1px inner border). The full-width drawers
 *  (Question, Signal Library) are inset by it on both sides, so they sit BETWEEN the
 *  strips rather than running under them and losing their edge content. */
export const DRAWER_TAB_W = 27;

/** The Signal Library bar (IndicesBar): above every drawer slot, its flyover and the strips. */
export const SIGNAL_BAR_Z = 73;

/** The bar at the foot of the timeline column (rail layout): below every drawer slot, so the rail's
 *  panels cover it. The Signal Library drawer opens above the bar, never over it. */
export const SIGNAL_BAR_IN_COLUMN_Z = 63;

/** The bar's height, in CSS px; the Signal Library drawer opens from its top edge. */
export const SIGNAL_BAR_H = 32;

/**
 * `order` with `id` moved to the top. Unknown ids leave it unchanged.
 * @param {readonly string[]} order bottom → top
 * @param {string} id
 * @returns {string[]}
 */
export function raiseDrawer(order, id) {
  if (!order.includes(id) || order[order.length - 1] === id) return [...order];
  return [...order.filter((d) => d !== id), id];
}

/**
 * The drawers that just OPENED between two open-states, in DRAWERS order — the ones to
 * raise. Closing never reorders, so a drawer keeps its place until it is opened again.
 * @param {Record<string, boolean>} prev
 * @param {Record<string, boolean>} next
 * @returns {string[]}
 */
export function newlyOpened(prev, next) {
  return DRAWERS.filter((d) => next[d] && !prev[d]);
}

/**
 * `{ log, chat, tsd, contract }` → z-index for the given order.
 * @param {readonly string[]} order bottom → top
 * @returns {Record<string, number>}
 */
export function drawerZMap(order) {
  return Object.fromEntries(order.map((d, i) => [d, DRAWER_Z_SLOTS[i]]));
}
